import React, { useLayoutEffect, useRef } from "react";
import GridLGHidden from "../components/GridComponent/components/GridLGHidden";
import Header from "../components/Header/Header";
import GridComponent from "../components/GridComponent/GridComponent";
import { LeftSideComponent } from "../components/Sidebar/Sidebar";
import GridTitle from "../components/GridComponent/components/GridTitle";
import { useNavigate } from "react-router-dom";
import Typography from "../components/Typography/Typography";
import { Form, Formik, FormikProps } from "formik";
import InputField from "../components/InputField/InputField";
import Button from "../components/Button/Button";
import { useToast } from "../context/ToastProvider";
import useUpdatePassword from "../store/Mutations/useUpdatePassword";
import * as Yup from "yup";
import ModalComponent from "../components/modals/ModalComponent";
import Divider from "../components/Divider/Divider";
import CheckEmailBox from "../components/CheckEmailBox/CheckEmailBox";

const ChangePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(8, "Must be at least 8 characters")
    .matches(/[a-z]/, "Must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Must contain at least one uppercase letter")
    .matches(/\d/, "Must contain at least one number"),
  repeatPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Please repeat your password"),
});

const ChangePassword = () => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [showCheckEmail, setShowCheckEmail] = React.useState(false);

  useLayoutEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    metaThemeColor && metaThemeColor.setAttribute("content", "#F5F5FC");
  }, []);

  const { mutate: updatePassword, isPending } = useUpdatePassword({
    onSuccess: () => {
      setModalOpen(true);
    },
    onError: (error) => {
      addToast({
        headerText: "Failed to change password",
        type: "error",
        message: error?.response?.data?.message || "Please try again",
      });
    },
  });

  const handleChangePassword = (values: any) => {
    updatePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
  };

  return (
    <div className="flex flex-col lg:flex-row bg-[#F5F5FC] lg:bg-transparent">
      <GridLGHidden>
        <Header backgroundColor="[#F5F5FC]" />
      </GridLGHidden>
      <GridComponent
        leftSideComponent={<LeftSideComponent />}
        containerClassNames={"lg:grid-cols-grid-chat-no-right-lg"}
        mainComponent={
          <MainComponent
            handleChangePassword={handleChangePassword}
            isPending={isPending}
            setShowCheckEmail={setShowCheckEmail}
          />
        }
        mainComponentContainerClassNames="!overflow-hidden"
      />
      <ModalComponent
        type="success"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="New password saved"
        description="Password changed successfully."
        primaryButtonText="Okay"
        onSubmit={() => {
          setModalOpen(false);
          navigate("/account-details");
        }}
      />
      {showCheckEmail && (
        <div className="w-full bg-modal-overlay  h-[100svh] justify-center items-center flex absolute top-0 left-0 z-50">
          <div className="lg:max-w-[700px] lg:h-svh flex items-center justify-center w-full">
            <CheckEmailBox />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangePassword;

const MainComponent = ({
  handleChangePassword,
  isPending,
  setShowCheckEmail,
}: {
  handleChangePassword: (values: any) => void;
  isPending: boolean;
  setShowCheckEmail: (value: boolean) => void;
}) => {
  const formikRef = useRef<FormikProps<any>>(null);
  const navigate = useNavigate();

  return (
    <div className="flex flex-1 flex-col lg:h-svh pb-[80px] lg:pb-0">
      <GridTitle
        title="Change password"
        backBtnClick={() => navigate("/account-details")}
      />
      <div className="lg:bg-white rounded-20 flex-1 h-full overflow-hidden lg:pr-24 lg:py-24 mb-24">
        <div className="lg:px-24 px-0 mt-24 lg:overflow-auto h-full pb-36 flex flex-col justify-between">
          <Formik
            innerRef={formikRef}
            initialValues={{
              currentPassword: "",
              newPassword: "",
              repeatPassword: "",
            }}
            validationSchema={ChangePasswordSchema}
            validateOnBlur={true}
            validateOnChange={false}
            onSubmit={handleChangePassword}
          >
            {({ values, setFieldValue, handleBlur, touched, errors }) => (
              <Form className="w-full flex flex-col gap-20 lg:gap-24">
                <div>
                  <Typography type="Body2" className="mb-12">
                    Current password
                  </Typography>
                  <div className="relative">
                    <InputField
                      placeholder="Password"
                      id="current-password"
                      type={"password"}
                      passwordWithIcon
                      value={values.currentPassword}
                      onChange={(e) =>
                        setFieldValue("currentPassword", e.target.value)
                      }
                      onBlur={handleBlur("currentPassword")}
                      error={
                        touched.currentPassword &&
                        typeof errors.currentPassword === "string"
                          ? (errors.currentPassword as string)
                          : undefined
                      }
                    />
                  </div>
                </div>

                <div>
                  <Typography type="Body2" className="mb-12">
                    New password
                  </Typography>
                  <div className="relative">
                    <InputField
                      placeholder="Password"
                      id="new-password"
                      type={"password"}
                      passwordWithIcon
                      value={values.newPassword}
                      onChange={(e) =>
                        setFieldValue("newPassword", e.target.value)
                      }
                      onBlur={handleBlur("newPassword")}
                      error={
                        touched.newPassword &&
                        typeof errors.newPassword === "string"
                          ? (errors.newPassword as string)
                          : undefined
                      }
                    />
                  </div>
                </div>

                <div>
                  <Typography type="Body2" className="mb-12">
                    Repeat password
                  </Typography>
                  <div className="relative">
                    <InputField
                      placeholder="Password"
                      id="repeat-password"
                      type={"password"}
                      passwordWithIcon
                      value={values.repeatPassword}
                      onChange={(e) =>
                        setFieldValue("repeatPassword", e.target.value)
                      }
                      onBlur={handleBlur("repeatPassword")}
                      error={
                        touched.repeatPassword &&
                        typeof errors.repeatPassword === "string"
                          ? (errors.repeatPassword as string)
                          : undefined
                      }
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>

          <div className="mt-24 shrink-0 max-w-[450px] mx-auto">
            <Button
              buttonType="primary"
              title="Save"
              type="button"
              loading={isPending}
              onClick={() => formikRef.current?.submitForm()}
            />

            <Divider className="my-24" />

            <Typography type="Body2" className="text-center !text-[#B9B9B9]">
              Forgot password?{" "}
              <a href="/forgot-password" className="text-primary underline">
                Click here
              </a>
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};
